// ゲスト記録の統合（クライアント側）。docs/AUTH.md の実装順序(5)。
//
// ## 何をする関数群か
//
// 🔴 **ゲストの記録は D1 に1行も無い**（localStorage だけ）。だから「別の端末でログイン」は
// アカウントを引き寄せる操作であると同時に、**この端末の記録の行き先を決める操作**でもある。
// 決めないまま持ち主を差し替えると、次の同期でサーバーの記録に上書きされて**黙って消える**。
//
// ⇒ ここは3段:
//   1. `collectLocalRecords()` … 各ゲームから「持ち込む記録」を集める（**登録簿経由**）
//   2. `previewMerge()`        … 書かずに★の増減だけ聞く（確認ダイアログの数字）
//   3. `commitMerge()`         … 実際に持ち込む（再送しても二重に足されない）
//
// ## 依存の向き
//
// ⚠️ **ゲームを名指しする import をここに書かない**。集める中身はゲーム固有なので、
//    [games/registry.ts](../../games/registry.ts) の `collectMerge`（動的 import）から取る。
//    ゲームを1本足したときの取りこぼしは `games/__tests__/registry.test.ts` が検出する。
import { GAMES } from '@/games/registry';
import type { GameMergeContribution } from '@/games/registry';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** サーバーへ送る `games` の中身（`gameKey` → 持ち込む記録）。 */
export type MergeContributions = Record<string, GameMergeContribution>;

/** 統合の下見。★がいくつからいくつになるか。 */
export interface MergePreview {
  /** 統合前のアカウントの★合計（全ゲーム）。 */
  account: number;
  /** 統合後の★合計（全ゲーム）。 */
  merged: number;
}

/**
 * この端末に残っている「持ち込む記録」を全ゲームぶん集める。
 * 記録が空のゲームは**含めない**（空の行をサーバーに作らないため＝
 * `reconcileProgressWithServer` が空の進捗を送らないのと同じ理由）。
 */
export async function collectLocalRecords(): Promise<MergeContributions> {
  const out: MergeContributions = {};
  for (const game of GAMES) {
    if (!game.collectMerge) continue;
    try {
      const contribution = await game.collectMerge();
      if (contribution.progress.length === 0 && contribution.scores.length === 0) continue;
      out[game.key] = contribution;
    } catch {
      // 1本のゲームの読み込みに失敗しても、ほかのゲームの記録は持ち込めるようにする。
      // ⚠️ ここで黙って落とすと**そのゲームの記録だけ消える**ので、統合を中止する
      //    ほうが安全に見えるが、失敗の原因は動的 import（＝オフライン時のチャンク欠け）が
      //    ほとんどで、中止すると誰もログインできなくなる。呼び出し側が件数で判断できるよう
      //    「集められたぶん」を返す。
      continue;
    }
  }
  return out;
}

/** 統合するものが1件でもあるか。 */
export function hasContributions(games: MergeContributions): boolean {
  return Object.keys(games).length > 0;
}

/**
 * 🔴 **ゲスト時代の自己ベストをランキング（`scores`）へ載せる**（2026-08-08 の本番実機テストで発覚）。
 *
 * `scores` と `player_stage_progress` は**同じ事実の2つの表現**なのに、書かれるきっかけが違う。
 * `scores` は「**クリアした瞬間**」に `submitScore` が書くだけなので、
 * **ゲスト中のクリアは誰も拾わない**（ゲストはサーバーへ送らないため）。
 * 結果、登録しても過去の★がランキングに出ず、「★合計6なのに順位は3」という
 * 説明できない食い違いが本番で出ていた。
 *
 * ⇒ 登録した直後に、この端末の自己ベストを**まとめて送り直す**。
 *
 * **なぜ統合エンドポイントを使わないか**: `openRegister` は**プロフィール編集でも同じ関数**で、
 * 統合には合算項目（プレイ回数・時間）があるため、編集のたびに二重加算される。
 * `/api/score` は `MAX` upsert ＝ **何度走っても結果が変わらない**ので門が要らない。
 *
 * 失敗は無視する（次のクリアで送り直される）。
 */
export async function backfillScores(games: MergeContributions): Promise<number> {
  const { submitScore } = await import('./leaderboard');
  let sent = 0;
  for (const [gameKey, contribution] of Object.entries(games)) {
    for (const s of contribution.scores) {
      try {
        await submitScore(s.stageKey, s.score, s.stars, gameKey);
        sent++;
      } catch {
        // 1件失敗しても残りは送る。ランキングは自己ベストなので、
        // 取りこぼしても次にそのステージをクリアした時点で追いつく。
      }
    }
  }
  return sent;
}

/**
 * 書かずに結果だけ聞く（確認ダイアログの数字）。**セッションが要る**＝ログイン直後に呼ぶこと。
 * 失敗したら null（呼び出し側は統合もログインも中止する）。
 */
export async function previewMerge(games: MergeContributions): Promise<MergePreview | null> {
  return request({ dryRun: true, games });
}

/**
 * 実際に持ち込む。
 *
 * ⚠️ **`requestId` は再試行しても同じ値を渡すこと**。合算する項目（プレイ回数・プレイ時間）が
 *    あるので、値を変えて送り直すと二重に足される。サーバーは直前の1件だけ覚えている。
 */
export async function commitMerge(
  requestId: string,
  games: MergeContributions,
): Promise<MergePreview | null> {
  return request({ requestId, games });
}

/** 統合の再試行で使い回す一意キー。 */
export function newMergeRequestId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function request(body: object): Promise<MergePreview | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/merge`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // セッション Cookie（`__Host-hsg_session`）を載せる。これが持ち込み先の唯一の根拠。
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { stars?: { account?: unknown; merged?: unknown } };
    const account = data.stars?.account;
    const merged = data.stars?.merged;
    if (typeof account !== 'number' || typeof merged !== 'number') return null;
    return { account, merged };
  } catch {
    return null;
  }
}
